import React, { useEffect, useState } from "react";
import { SLOT_META, STATUS_LABEL } from "@/data/reel";
import { Smartphone } from "lucide-react";

/**
 * DisplayCampaignCard — tight magazine layout. Four IAB slots, no labels
 * around the ads. When a slot has multiple creatives, they auto-rotate
 * in place every 3.5s.
 *
 *   ┌──────────────────┬────────────┐
 *   │ 320×50 (rotates) │            │
 *   ├──────────────────┤  320×480   │
 *   │                  │  (rotates) │
 *   │ 300×250 (rotates)│            │
 *   ├──────────────────┴────────────┤
 *   │   728×90 leaderboard          │
 *   └───────────────────────────────┘
 *
 * Drop new files into the matching slot array in /data/reel.js — no other
 * code changes needed.
 */

const ROTATE_MS = 3500;

const useRotation = (items) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!items || items.length <= 1) return undefined;
    const t = setInterval(
      () => setIdx((i) => (i + 1) % items.length),
      ROTATE_MS
    );
    return () => clearInterval(t);
  }, [items]);
  if (!items || items.length === 0) return null;
  return items[idx % items.length];
};

const Slot = ({ items, sizeKey, client, gridArea, className = "" }) => {
  const current = useRotation(items);
  if (!current) return null;
  const meta = SLOT_META[sizeKey];
  const isVideo = /\.mp4($|\?)/i.test(current.src);
  return (
    <div
      style={{
        gridArea,
        aspectRatio: meta.aspect,
        background:
          "linear-gradient(180deg, #d6d6d8 0%, #b4b4b8 50%, #8e8e93 100%)",
      }}
      className={`w-full rounded-md overflow-hidden p-[1.5px] self-start ${className}`}
      data-testid={`slot-${sizeKey}`}
    >
      <div className="w-full h-full bg-[var(--af-navy-deep)] rounded-[3px] overflow-hidden flex items-center justify-center">
      {isVideo ? (
        <video
          key={current.src}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={current.poster}
          className="w-full h-full object-cover"
        >
          <source src={current.src.replace(/\.mp4($|\?)/i, ".webm$1")} type="video/webm" />
          <source src={current.src} type="video/mp4" />
        </video>
      ) : (
        <img
          src={current.src}
          alt={`${client}, ${meta.name}`}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      )}
      </div>
    </div>
  );
};

const DisplayCampaignCard = ({ campaign, compact = false }) => {
  const { client, industry, status, slots } = campaign;
  const banner320 = slots?.banner320 || [];
  const banner728 = slots?.banner728 || [];
  const mrec = slots?.mrec || [];
  const interstitial = slots?.interstitial || [];
  const halfpage = slots?.halfpage || [];

  // Right column: halfpage takes priority when present (it's typically the newer / current flight);
  // otherwise fall back to interstitial.
  const rightItems = halfpage.length > 0 ? halfpage : interstitial;
  const rightSizeKey = halfpage.length > 0 ? "halfpage" : "interstitial";

  // Magazine grid:
  //   row 1  col 1 = banner320 (top-left)
  //   row 2  col 1 = mrec (main left)
  //   col 2 spans rows 1-2 = interstitial (tall right)
  //   row 3 spans both cols = banner728 (full-width bottom)
  const gridStyle = {
    gridTemplateColumns: "3fr 2fr",
    gridTemplateAreas: `
      "tl r"
      "ml r"
      "b  b"
    `,
  };

  return (
    <div
      className="surface p-3 sm:p-6 flex flex-col w-full max-w-[440px] mx-auto"
      data-testid={`campaign-card-${campaign.id}`}
    >
      {/* Minimal header — client + status/industry chips */}
      <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3 mb-3 sm:mb-5">
        <div
          className={`font-display ${compact ? "text-lg" : "text-lg sm:text-xl"} leading-tight`}
        >
          {client}
        </div>
        <div className="flex flex-wrap gap-1.5 sm:gap-2">
          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold bg-[var(--af-lime)] text-[var(--af-navy)] px-2 py-0.5 rounded-sm">
            {STATUS_LABEL[status]}
          </span>
          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold border border-[var(--af-cream)]/30 text-[var(--af-cream)]/80 px-2 py-0.5 rounded-sm">
            {industry}
          </span>
        </div>
      </div>

      {/* Magazine grid — desktop only (md+) */}
      <div
        className="hidden md:grid gap-3 items-start"
        style={gridStyle}
        data-testid="campaign-magazine-grid"
      >
        <Slot items={banner320} sizeKey="banner320" client={client} gridArea="tl" />
        <Slot items={mrec} sizeKey="mrec" client={client} gridArea="ml" />
        <Slot items={rightItems} sizeKey={rightSizeKey} client={client} gridArea="r" />
        <Slot items={banner728} sizeKey="banner728" client={client} gridArea="b" />
      </div>

      {/* Mobile fallback — single representative slot only.
          Preference: tall halfpage/interstitial > medium rectangle > banners.
          Keeps the page scrollable while still showing the campaign's strongest creative. */}
      <div className="md:hidden flex flex-col gap-3">
        {rightItems.length > 0 ? (
          <Slot items={rightItems} sizeKey={rightSizeKey} client={client} />
        ) : mrec.length > 0 ? (
          <Slot items={mrec} sizeKey="mrec" client={client} />
        ) : banner320.length > 0 ? (
          <Slot items={banner320} sizeKey="banner320" client={client} />
        ) : (
          <Slot items={banner728} sizeKey="banner728" client={client} />
        )}
      </div>

      {/* Mobile-best-viewed note */}
      <div
        className="mt-3 sm:mt-4 flex items-start gap-2 text-[11px] text-[var(--af-lime)]/90 italic leading-snug"
        data-testid={`campaign-mobile-note-${campaign.id}`}
      >
        <Smartphone size={13} className="mt-0.5 flex-shrink-0" />
        <span>
          Best viewed on a mobile device, may look pixelated at desktop sizes.
        </span>
      </div>
    </div>
  );
};

export default DisplayCampaignCard;
export { DisplayCampaignCard };
